import React from 'react';

type Props = {
  children: React.ReactNode;
  onReset?: () => void;
};

type State = {
  hasError: boolean;
  error?: Error | null;
  info?: React.ErrorInfo | null;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to console; leave room to integrate a reporting service later
    console.error('ErrorBoundary caught:', error, info);
    this.setState({ error, info });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null });
    if (this.props.onReset) this.props.onReset();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-destructive">Something went wrong</h4>
              <p className="text-xs text-muted-foreground mt-1">An error occurred while rendering this part of the app. You can try to recover below.</p>
              <details className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap max-h-40 overflow-auto">
                <summary className="cursor-pointer text-[10px] text-muted-foreground">Show error details</summary>
                <pre className="mt-2 text-[11px]">{String(this.state.error)}{this.state.info ? `\n\n${this.state.info.componentStack}` : ''}</pre>
              </details>

              <div className="mt-3 flex gap-2">
                <button className="px-3 py-1 rounded-md border border-border/30 bg-muted/10 text-sm" onClick={this.handleReset}>
                  Exit edit mode
                </button>
                <button
                  className="px-3 py-1 rounded-md border border-border/30 bg-background/30 text-sm"
                  onClick={() => window.location.reload()}
                >
                  Reload page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children as React.ReactElement;
  }
}
